import { AssignmentAuditDetailsResponse } from '@6grain/masofa-monolith-api-client/common/model/assignmentAuditDetailsResponse';
import { AssignmentDataRecordGroupResponse } from '@6grain/masofa-monolith-api-client/common/model/assignmentDataRecordGroupResponse';
import { AssignmentDataRecordResponse } from '@6grain/masofa-monolith-api-client/common/model/assignmentDataRecordResponse';
import { SatelliteAssignmentProductResponse } from '@6grain/masofa-monolith-api-client/common/model/satelliteAssignmentProductResponse';
import { formatDatabaseSizeBytes } from '@app/pages/admin/orchestration-database-connections.util';
import { orchestrationSatelliteProductUrl } from '@app/pages/admin/orchestration-identity.util';
import { fileStorageDownloadUrl } from '@app/pages/admin/orchestration-object-storage.util';

export interface ProcessDataEffect {
  command: string;
  database: string;
  table: string;
  operation: string;
  description: string;
}

export interface AssignmentDataRecord {
  id: string;
  label: string | null;
  state: string | null;
  changedAt?: string | null;
  operation: string;
  bytes: number | null;
}

export interface AssignmentDataRecordGroup {
  database: string;
  table: string;
  kind: string;
  count: number;
  bytesWritten: number;
  items: AssignmentDataRecord[];
}

export function normalizeProcessDataEffects(value: unknown): ProcessDataEffect[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map(item => (item && typeof item === 'object' ? item as Record<string, unknown> : null))
    .filter((item): item is Record<string, unknown> => !!item)
    .map(item => ({
      command: stringOrEmpty(item['command']) || '*',
      database: stringOrEmpty(item['database']),
      table: stringOrEmpty(item['table']),
      operation: stringOrEmpty(item['operation']),
      description: stringOrEmpty(item['description']),
    }))
    .filter(item => !!item.table && !!item.operation);
}

export function readAssignmentDataRecords(assignment: AssignmentAuditDetailsResponse | null): AssignmentDataRecordGroup[] {
  if (!assignment) {
    return [];
  }
  return normalizeAssignmentDataRecordGroups(assignment.dataRecords);
}

export function composeAssignmentDataRecords(
  assignment: AssignmentAuditDetailsResponse | null,
  products: SatelliteAssignmentProductResponse[] = [],
): AssignmentDataRecordGroup[] {
  const fromApi = readAssignmentDataRecords(assignment);
  if (fromApi.length) {
    return clarifySatelliteProductDownloadStates(fromApi, products);
  }
  if (!assignment?.id) {
    return [];
  }

  const groups: AssignmentDataRecordGroup[] = [];
  addGroup(groups, 'MasofaCommon', 'Assignments', 'audit', [{
    id: assignment.id,
    label: assignment.executionDefinitionKey ?? null,
    state: assignment.state ?? null,
    changedAt: assignment.completedAt ?? assignment.startedAt ?? assignment.createdAt,
    operation: 'Insert',
    bytes: null,
  }]);
  addGroup(
    groups,
    'MasofaCommon',
    'AssignmentStateTransitions',
    'audit',
    (assignment.stateHistory ?? []).map(item => ({
      id: item.id ?? '',
      label: item.details ?? null,
      state: item.state ?? null,
      changedAt: item.changedAt,
      operation: 'Insert',
      bytes: null,
    })),
  );
  addGroup(
    groups,
    'MasofaCommon',
    'CommandExecutions',
    'audit',
    (assignment.commandExecutions ?? []).map(item => ({
      id: item.id ?? '',
      label: item.stepKey ?? item.commandType ?? null,
      state: item.state ?? null,
      changedAt: item.completedAt ?? item.startedAt,
      operation: 'Insert',
      bytes: null,
    })),
  );
  addGroup(
    groups,
    'MasofaCommon',
    'PublishedEvents',
    'audit',
    (assignment.publishedEvents ?? []).map(item => ({
      id: item.id ?? '',
      label: item.eventKey ?? item.kind ?? null,
      state: item.state ?? null,
      changedAt: item.completedAt ?? item.startedAt,
      operation: 'Insert',
      bytes: null,
    })),
  );
  addGroup(
    groups,
    'MasofaCommon',
    'ExternalServiceCalls',
    'audit',
    (assignment.externalServiceCalls ?? []).map(item => ({
      id: item.id ?? '',
      label: [item.serviceName, item.httpMethod].filter(Boolean).join(' ') || null,
      state: item.statusCode != null ? String(item.statusCode) : item.error ?? null,
      changedAt: item.createAt,
      operation: 'Insert',
      bytes: null,
    })),
  );
  addGroup(
    groups,
    'MasofaCommon',
    'SatelliteProducts',
    'domain',
    products.map(item => ({
      id: item.id ?? '',
      label: item.externalProductId ?? item.provider ?? null,
      state: item.downloadState ?? item.processingStage ?? null,
      changedAt: item.discoveredAt ?? item.originDate,
      operation: 'Upsert',
      bytes: null,
    })),
  );
  return clarifySatelliteProductDownloadStates(groups, products);
}

export type DataEffectOperationSeverity = 'success' | 'info' | 'warn' | 'danger' | 'secondary';

export function dataEffectOperationSeverity(operation: string): DataEffectOperationSeverity {
  switch (operation) {
    case 'Insert':
      return 'success';
    case 'Update':
    case 'Upsert':
      return 'info';
    case 'Delete':
      return 'danger';
    default:
      return 'secondary';
  }
}

export function assignmentDataRecordCount(groups: AssignmentDataRecordGroup[]): number {
  return groups.reduce((total, group) => total + group.count, 0);
}

export type FlattenedAssignmentDataRecord = AssignmentDataRecord & {
  database: string;
  table: string;
  kind: string;
  stateLabel: string | null;
  stateSeverity: DataEffectOperationSeverity;
  operationSeverity: DataEffectOperationSeverity;
  live: boolean;
  statusUntil: string | null;
  bytesLabel: string;
  productAnalysisUrl: string | null;
  fileDownloadUrl: string | null;
};

export function flattenAssignmentDataRecords(
  groups: AssignmentDataRecordGroup[],
): FlattenedAssignmentDataRecord[] {
  const assignmentChangedAt = groups.find(group => group.table === 'Assignments')?.items[0]?.changedAt ?? null;
  return groups.flatMap(group => group.items.map((item, index) => ({
    ...item,
    database: group.database,
    table: group.table,
    kind: group.kind,
    stateLabel: dataRecordStateLabel(item.state, group.table),
    stateSeverity: dataRecordStateSeverity(item.state, group.table),
    operationSeverity: dataEffectOperationSeverity(item.operation),
    live: isLiveDataRecord(group.table, item.state),
    statusUntil: snapshotStatusUntil(group.table, group.items, index, assignmentChangedAt),
    bytesLabel: formatDatabaseSizeBytes(item.bytes),
    productAnalysisUrl: group.table === 'SatelliteProducts'
      ? orchestrationSatelliteProductUrl(item.id)
      : null,
    fileDownloadUrl: group.table === 'FileStorageItems'
      ? fileStorageDownloadUrl(item.id)
      : null,
  })));
}

export function assignmentDataRecordBytesLabel(groups: AssignmentDataRecordGroup[]): string {
  return formatDatabaseSizeBytes(groups.reduce((sum, group) => sum + group.bytesWritten, 0));
}

export function dataRecordRowKey(row: Pick<FlattenedAssignmentDataRecord, 'database' | 'table' | 'id'>): string {
  return `${row.database}\0${row.table}\0${row.id}`;
}

export function syncAssignmentDataRecordRows(
  current: FlattenedAssignmentDataRecord[],
  incoming: FlattenedAssignmentDataRecord[],
): FlattenedAssignmentDataRecord[] {
  if (!current.length) {
    return incoming;
  }
  const sameIdentity = current.length === incoming.length
    && current.every((row, index) => dataRecordRowKey(row) === dataRecordRowKey(incoming[index]));
  if (sameIdentity) {
    incoming.forEach((next, index) => assignChangedRecordFields(current[index], next));
    return current;
  }
  const byKey = new Map(current.map(row => [dataRecordRowKey(row), row]));
  return incoming.map(next => {
    const existing = byKey.get(dataRecordRowKey(next));
    if (!existing) {
      return next;
    }
    assignChangedRecordFields(existing, next);
    return existing;
  });
}

function assignChangedRecordFields(
  target: FlattenedAssignmentDataRecord,
  source: FlattenedAssignmentDataRecord,
): void {
  if (target.label !== source.label) {
    target.label = source.label;
  }
  if (target.state !== source.state) {
    target.state = source.state;
  }
  if (target.stateLabel !== source.stateLabel) {
    target.stateLabel = source.stateLabel;
  }
  if (target.stateSeverity !== source.stateSeverity) {
    target.stateSeverity = source.stateSeverity;
  }
  if (target.changedAt !== source.changedAt) {
    target.changedAt = source.changedAt;
  }
  if (target.kind !== source.kind) {
    target.kind = source.kind;
  }
  if (target.operation !== source.operation) {
    target.operation = source.operation;
  }
  if (target.operationSeverity !== source.operationSeverity) {
    target.operationSeverity = source.operationSeverity;
  }
  if (target.live !== source.live) {
    target.live = source.live;
  }
  if (target.statusUntil !== source.statusUntil) {
    target.statusUntil = source.statusUntil;
  }
  if (target.bytes !== source.bytes) {
    target.bytes = source.bytes;
  }
  if (target.bytesLabel !== source.bytesLabel) {
    target.bytesLabel = source.bytesLabel;
  }
  if (target.productAnalysisUrl !== source.productAnalysisUrl) {
    target.productAnalysisUrl = source.productAnalysisUrl;
  }
  if (target.fileDownloadUrl !== source.fileDownloadUrl) {
    target.fileDownloadUrl = source.fileDownloadUrl;
  }
}

export function formatTimeInStatus(
  changedAt: string | null | undefined,
  now = Date.now(),
  until?: string | null,
): string | null {
  if (!changedAt) {
    return null;
  }
  const startedAt = new Date(changedAt).getTime();
  if (Number.isNaN(startedAt)) {
    return null;
  }
  const endedAt = until ? new Date(until).getTime() : now;
  if (Number.isNaN(endedAt)) {
    return formatElapsedDuration(now - startedAt);
  }
  return formatElapsedDuration(endedAt - startedAt);
}

export function formatElapsedDuration(milliseconds: number): string {
  const elapsed = Math.max(0, milliseconds);
  if (elapsed < 1000) {
    return `${Math.round(elapsed)} ms`;
  }
  const seconds = Math.floor(elapsed / 1000);
  if (seconds < 60) {
    return `${seconds} s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) {
    return `${minutes} min ${remainingSeconds} s`;
  }
  const hours = Math.floor(minutes / 60);
  return `${hours} h ${minutes % 60} min`;
}

export function hasLiveStatusDuration(groups: AssignmentDataRecordGroup[]): boolean {
  return groups.some(group =>
    group.items.some(item => !!item.changedAt && isLiveDataRecord(group.table, item.state)),
  );
}

export function isSnapshotDataRecordTable(table: string | null | undefined): boolean {
  return table === 'AssignmentStateTransitions';
}

export function isLiveDataRecord(
  table: string | null | undefined,
  state: string | null | undefined,
): boolean {
  return !isSnapshotDataRecordTable(table) && isLiveDataRecordState(state);
}

export function isLiveDataRecordState(state: string | null | undefined): boolean {
  return state === 'Downloading'
    || state === 'Queued'
    || state === 'Pending'
    || state === 'Running'
    || state === 'Processing'
    || state === 'Cancelling';
}

function snapshotStatusUntil(
  table: string,
  items: AssignmentDataRecord[],
  index: number,
  assignmentChangedAt?: string | null,
): string | null {
  if (!isSnapshotDataRecordTable(table)) {
    return null;
  }
  const nextChangedAt = items[index + 1]?.changedAt;
  if (typeof nextChangedAt === 'string' && nextChangedAt) {
    return nextChangedAt;
  }
  if (index === items.length - 1 && typeof assignmentChangedAt === 'string' && assignmentChangedAt) {
    return assignmentChangedAt;
  }
  return null;
}

export function dataRecordStateLabel(
  state: string | null | undefined,
  table?: string | null,
): string | null {
  if (!state) {
    return null;
  }
  if (isSnapshotDataRecordTable(table) && isLiveDataRecordState(state)) {
    switch (state) {
      case 'Pending':
        return 'Was pending';
      case 'Running':
      case 'Processing':
        return 'Was running';
      case 'Cancelling':
        return 'Was cancelling';
      case 'Downloading':
        return 'Was downloading';
      case 'Queued':
        return 'Was queued';
      default:
        return state;
    }
  }
  return state;
}

export function dataRecordStateSeverity(
  state: string | null | undefined,
  table?: string | null,
): DataEffectOperationSeverity {
  if (isSnapshotDataRecordTable(table) && isLiveDataRecordState(state)) {
    return 'secondary';
  }
  switch (state) {
    case 'Completed':
    case 'Downloaded':
    case 'Processed':
    case 'Succeeded':
    case 'Active':
      return 'success';
    case 'Downloading':
    case 'Running':
    case 'Processing':
      return 'info';
    case 'Failed':
    case 'DownloadFailed':
    case 'Download failed':
      return 'danger';
    case 'Cancelled':
    case 'Cancelling':
      return 'warn';
    default:
      return 'secondary';
  }
}

export function hasInProgressSatelliteDownload(groups: AssignmentDataRecordGroup[]): boolean {
  return groups.some(group =>
    (group.table === 'SatelliteProducts' || group.table === 'SatelliteDownloadItems')
    && group.items.some(item =>
      item.state === 'Downloading' || item.state === 'Queued' || item.state === 'Pending'),
  );
}

export function clarifySatelliteProductDownloadStates(
  groups: AssignmentDataRecordGroup[],
  products: SatelliteAssignmentProductResponse[] = [],
): AssignmentDataRecordGroup[] {
  const downloadStateByProductId = new Map<string, string>();
  for (const group of groups) {
    if (group.table !== 'SatelliteDownloadItems') {
      continue;
    }
    for (const item of group.items) {
      if (item.label && item.state) {
        downloadStateByProductId.set(item.label, item.state);
      }
    }
  }
  for (const product of products) {
    if (product.id && product.downloadState) {
      downloadStateByProductId.set(product.id, product.downloadState);
    }
  }
  if (!downloadStateByProductId.size) {
    return groups;
  }

  return groups.map(group => {
    if (group.table !== 'SatelliteProducts') {
      return group;
    }
    return {
      ...group,
      items: group.items.map(item => ({
        ...item,
        state: displaySatelliteProductState(item.state, downloadStateByProductId.get(item.id) ?? null),
      })),
    };
  });
}

export function displaySatelliteProductState(
  processingStage: string | null | undefined,
  downloadState: string | null | undefined,
): string | null {
  if (downloadState === 'Pending' && (processingStage === 'Downloading' || !processingStage)) {
    return 'Queued';
  }
  if (downloadState === 'Downloading') {
    return 'Downloading';
  }
  if (downloadState === 'Failed' || downloadState === 'DownloadFailed') {
    return 'DownloadFailed';
  }
  return processingStage ?? downloadState ?? null;
}

function normalizeAssignmentDataRecordGroups(
  value: AssignmentDataRecordGroupResponse[] | null | undefined,
): AssignmentDataRecordGroup[] {
  if (!value?.length) {
    return [];
  }
  return value
    .map(item => {
      const items = normalizeAssignmentDataRecords(item.items);
      const count = typeof item.count === 'number' ? item.count : items.length;
      const bytesWritten = readNonNegativeNumber(item.bytesWritten)
        ?? items.reduce((sum, row) => sum + (row.bytes ?? 0), 0);
      return {
        database: stringOrEmpty(item.database) || 'MasofaCommon',
        table: stringOrEmpty(item.table),
        kind: stringOrEmpty(item.kind) || 'audit',
        count,
        bytesWritten,
        items,
      };
    })
    .filter(item => !!item.table && item.count > 0);
}

function normalizeAssignmentDataRecords(
  value: AssignmentDataRecordResponse[] | null | undefined,
): AssignmentDataRecord[] {
  if (!value?.length) {
    return [];
  }
  return value
    .filter(item => !!stringOrEmpty(item.id))
    .map(item => ({
      id: stringOrEmpty(item.id),
      label: stringOrNull(item.label),
      state: stringOrNull(item.state),
      changedAt: stringOrNull(item.changedAt) ?? undefined,
      operation: stringOrEmpty(item.operation),
      bytes: readNonNegativeNumber(item.bytes),
    }));
}

function addGroup(
  groups: AssignmentDataRecordGroup[],
  database: string,
  table: string,
  kind: string,
  items: AssignmentDataRecord[],
): void {
  const rows = items.filter(item => !!item.id);
  if (!rows.length) {
    return;
  }
  groups.push({ database, table, kind, count: rows.length, bytesWritten: rows.reduce((sum, row) => sum + (row.bytes ?? 0), 0), items: rows });
}

function stringOrEmpty(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function stringOrNull(value: unknown): string | null {
  const text = stringOrEmpty(value);
  return text || null;
}

function readNonNegativeNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
}
